import React, { useState } from 'react';
import { Box, Button, Chip, Collapse, Stack, Typography } from '@mui/material';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const sectionTitleSx = {
  color: 'var(--wb-text-faint)',
  fontWeight: 600,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  display: 'block',
  mt: 2,
  mb: 0.75
};

const STOP_REASONS = {
  tiempo: 'el tiempo máximo',
  'datos escaneados': 'el máximo de datos escaneados'
};

const stoppedText = (reason) => (reason ? STOP_REASONS[reason] || `el máximo de ${reason}` : null);

/**
 * Informe de un Análisis Profundo (deep_report de agent-runtime):
 * conclusión, hallazgos con su evidencia, advertencias, preguntas para
 * seguir y, plegado, cómo se investigó (plan y consultas SQL).
 */
const DeepAnalysisReport = ({ report, onFollowUp }) => {
  const [showMethod, setShowMethod] = useState(false);
  const findings = report.findings || [];
  const caveats = report.caveats || [];
  const nextQuestions = report.next_questions || [];
  const plan = report.plan || [];
  const queries = report.queries || [];

  return (
    <Box sx={{ color: 'var(--wb-text-strong)', maxWidth: 820 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
        <Chip
          size="small"
          icon={<QueryStatsIcon sx={{ color: 'var(--wb-accent) !important' }} />}
          label="Análisis profundo"
          sx={{ backgroundColor: 'var(--wb-accent-tint)', color: 'var(--wb-accent)', fontWeight: 600 }}
        />
        {report.partial && (
          <Chip
            size="small"
            label="Parcial"
            sx={{ backgroundColor: 'var(--wb-warning-12)', color: 'var(--wb-warning)', fontWeight: 600 }}
          />
        )}
      </Stack>

      <Typography variant="body1" sx={{ color: 'var(--wb-text-strong)', fontWeight: 500, whiteSpace: 'pre-wrap' }}>
        {report.conclusion}
      </Typography>

      {findings.length > 0 && (
        <>
          <Typography variant="caption" sx={sectionTitleSx}>Hallazgos</Typography>
          <Box component="ol" sx={{ m: 0, pl: 0, listStyle: 'none' }}>
            {findings.map((finding, index) => (
              <Box
                component="li"
                key={`${index}-${finding.title}`}
                sx={{
                  py: 1,
                  pl: 1.5,
                  mb: 1,
                  borderLeft: '3px solid var(--wb-accent)',
                  backgroundColor: 'var(--wb-overlay-08)',
                  borderRadius: '0 8px 8px 0'
                }}
              >
                <Typography variant="body2" sx={{ color: 'var(--wb-text-strong)', fontWeight: 600 }}>
                  {index + 1}. {finding.title}
                </Typography>
                {finding.impact && (
                  <Typography variant="body2" sx={{ color: 'var(--wb-text-soft)' }}>Impacto: {finding.impact}</Typography>
                )}
                {finding.evidence && (
                  <Typography variant="body2" sx={{ color: 'var(--wb-text-soft)' }}>Evidencia: {finding.evidence}</Typography>
                )}
              </Box>
            ))}
          </Box>
        </>
      )}

      {caveats.length > 0 && (
        <>
          <Typography variant="caption" sx={sectionTitleSx}>Advertencias</Typography>
          {caveats.map((caveat) => (
            <Stack key={caveat} direction="row" spacing={1} alignItems="flex-start" sx={{ py: 0.25 }}>
              <WarningAmberIcon fontSize="small" sx={{ color: 'var(--wb-warning)', mt: '2px' }} />
              <Typography variant="body2" sx={{ color: 'var(--wb-text-soft)' }}>{caveat}</Typography>
            </Stack>
          ))}
        </>
      )}

      {nextQuestions.length > 0 && (
        <>
          <Typography variant="caption" sx={sectionTitleSx}>Preguntas para seguir</Typography>
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
            {nextQuestions.map((question) => (
              <Chip
                key={question}
                label={question}
                onClick={onFollowUp ? () => onFollowUp(question) : undefined}
                variant="outlined"
                sx={{
                  height: 'auto',
                  py: 0.5,
                  color: 'var(--wb-accent)',
                  borderColor: 'var(--wb-accent-40)',
                  '& .MuiChip-label': { whiteSpace: 'normal' },
                  '&:hover': { backgroundColor: 'var(--wb-accent-tint) !important' }
                }}
              />
            ))}
          </Stack>
        </>
      )}

      {(plan.length > 0 || queries.length > 0) && (
        <>
          <Button
            size="small"
            onClick={() => setShowMethod((value) => !value)}
            endIcon={showMethod ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            aria-expanded={showMethod}
            sx={{ mt: 2, px: 0, textTransform: 'none', color: 'var(--wb-text-faint)' }}
          >
            Cómo se investigó ({plan.length} pasos, {queries.length} consultas)
          </Button>
          <Collapse in={showMethod} unmountOnExit>
            <Box sx={{ pl: 1.5, borderLeft: '2px solid var(--wb-border-20)', mt: 0.5 }}>
              {report.objective && (
                <Typography variant="body2" sx={{ color: 'var(--wb-text-soft)' }}>Objetivo: {report.objective}</Typography>
              )}
              {report.baseline && (
                <Typography variant="body2" sx={{ color: 'var(--wb-text-soft)' }}>Comparado contra: {report.baseline}</Typography>
              )}
              {plan.length > 0 && (
                <Box component="ol" sx={{ color: 'var(--wb-text-soft)', pl: 2.5, my: 1 }}>
                  {plan.map((step) => (
                    <Typography component="li" variant="body2" key={step.id}>{step.question}</Typography>
                  ))}
                </Box>
              )}
              {queries.map((query, index) => (
                <Box
                  key={`${index}-${query.sql}`}
                  component="pre"
                  sx={{
                    m: 0,
                    mb: 1,
                    p: 1,
                    borderRadius: 1,
                    overflowX: 'auto',
                    fontSize: '0.78rem',
                    color: 'var(--wb-text)',
                    backgroundColor: 'var(--wb-bg-surface)',
                    border: '1px solid var(--wb-border-16)'
                  }}
                >
                  {query.sql}
                </Box>
              ))}
              {stoppedText(report.stopped_reason) && (
                <Typography variant="caption" sx={{ color: 'var(--wb-text-faint)' }}>
                  La investigación se detuvo al llegar a {stoppedText(report.stopped_reason)}.
                </Typography>
              )}
            </Box>
          </Collapse>
        </>
      )}
    </Box>
  );
};

export default DeepAnalysisReport;
